import React from 'react';
import { FormProvider, useForm, WatchObserver } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Layout, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import { useQuery } from 'hooks/useQuery';
import { Statuses } from 'constants/status';
import { debounce, removeEmptyValues } from 'utils/helpers';
import { mapSelect } from 'utils/mappings';
import { InputField } from 'components/form/base/InputField';
import { Select } from 'components/form/base/Select';
import { DatePicker } from 'components/form/base/DatePicker';
import { defaultDateFormat } from 'components/form/base/RangePicker';

import { MINIMAL_YEAR } from '../PurchasePlansForm/constants';
import { FILTER_STATUS_OPTIONS } from './constants';

export interface FilterValues {
  nameQuery: string;
  status: Statuses | '';
  year: Dayjs | null;
}

export const Filters = () => {
  const { year, ...query } = useQuery();
  const navigate = useNavigate();

  const methods = useForm<FilterValues>({
    defaultValues: {
      nameQuery: '',
      status: '',
      year: year ? dayjs().year(Number(year)) : null,
      ...query,
    },
  });

  const updateFilters = (filterValues: Record<string, string>) => {
    const queryString = new URLSearchParams(filterValues).toString();
    navigate(`${location.pathname}?${queryString}`);
  };

  const debounceSubmit = React.useCallback(
    debounce(({ status, year, ...values }: FilterValues) => {
      const preparedValues = {
        ...values,
        year: dayjs(year, defaultDateFormat).get('y'),
        status: mapSelect(status),
      };

      updateFilters(removeEmptyValues(preparedValues));
    }, 500),
    []
  );

  React.useEffect(() => {
    const subscription = methods.watch(
      methods.handleSubmit(
        debounceSubmit
      ) as unknown as WatchObserver<FilterValues>
    );
    return () => subscription.unsubscribe();
  }, [methods.handleSubmit, methods.watch]);

  return (
    <Layout.Content
      style={{ background: '#fff', paddingTop: 16, paddingBottom: 1 }}
    >
      <Typography.Title level={5} style={{ marginBottom: 24 }}>
        Фильтры
      </Typography.Title>
      <FormProvider {...methods}>
        <form className='ant-form-vertical'>
          <InputField
            name='nameQuery'
            placeholder='Введите название'
            label='Название'
            maxLength={255}
          />
          <Select
            name='status'
            options={FILTER_STATUS_OPTIONS}
            label='Статус'
          />
          <DatePicker
            name='year'
            label='Год'
            picker='year'
            dateFormatList='YYYY'
            minDateRestriction={dayjs().year(MINIMAL_YEAR)}
            maxDateRestriction={dayjs()}
            placeholder='Выберите год'
          />
        </form>
      </FormProvider>
    </Layout.Content>
  );
};
